import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import PageTableHeader from "../../../../components/pageTableHeader/pageTableHeader";
import CommonTable from "../../../../components/table/table";
import CommonModal from "../../../../components/modals/commonModel";
import DefaultButton from "../../../../components/buttons/defaultButton";
import DefaultSearchSelectInput from "../../../../components/inputs/defaultSearchSelectInput";
import DefaultDatePicker from "../../../../components/inputs/defaultDatePicker";
import { 
  HiOutlineClock, 
  HiOutlineUsers,
  HiOutlineArrowLeft,
  HiOutlinePencil,
  HiOutlineX,
  HiOutlineSwitchHorizontal
} from "react-icons/hi";
import { IoMdClose } from "react-icons/io";
import NoData from "../../../../components/noData/noData";
import moment from "moment";
import toast from "react-hot-toast";
import { 
  getShiftDetail,
  bulkAssignShift,
  getShiftList
} from "../../../../../services/workspace/attendanceManagement.service";
import { getUserList } from "../../../../../services/workspace/user.service";

const items = [
  { label: "HR Shift Editor", path: "/hr/shift-editor" },
  { label: "Detail", path: "" }];

// User Card Component (similar to team detail page)
const UserCard = ({ assignment, onRemove, isRemoving = false }) => {
  const user = assignment?.user || {};
  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow relative group">
      {/* Remove Button */}
      {onRemove && (
        <button
          onClick={onRemove}
          disabled={isRemoving}
          className="absolute top-2 right-2 p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-full opacity-0 group-hover:opacity-100 transition-all z-10"
          title="Remove from shift"
        >
          <IoMdClose className="w-4 h-4" />
        </button>
      )}
      
      <div className="flex items-start gap-4">
        {/* Avatar */}
        <div className="w-12 h-12 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
          {user.image ? (
            <img
              src={user.image}
              alt={user.name}
              className="w-12 h-12 rounded-full object-cover"
            />
          ) : (
            <span className="text-primary-600 font-semibold text-lg">
              {user.name?.charAt(0)?.toUpperCase() || "U"}
            </span>
          )}
        </div>
        
        {/* User Info */}
        <div className="flex-1 min-w-0">
          <h3 className="text-primary-600 font-semibold text-base mb-1 truncate">
            {user.name || "Unknown"}
          </h3>
          <div className="flex flex-col gap-1 text-sm text-gray-600">
            {user.code && (
              <span className="truncate">
                <span className="font-medium">Code:</span> {user.code}
              </span>
            )}
            {user.email && (
              <span className="truncate">
                <span className="font-medium">Email:</span> {user.email}
              </span>
            )}
            {user.phone && (
              <span className="truncate">
                <span className="font-medium">Phone:</span> {user.country_code || ""} {user.phone}
              </span>
            )}
            {assignment.effective_from && (
              <span className="text-xs text-gray-500">
                Effective From: {moment(assignment.effective_from).format("DD MMM YYYY")}
              </span>
            )}
          </div>
        </div>
        
        {/* Status Badge */}
        <div className="mt-6 flex-shrink-0">
          <span
            className={`px-3 py-1 rounded-full text-xs font-medium ${
              assignment.status === 1
                ? "bg-green-100 text-green-800"
                : "bg-gray-100 text-gray-800"
            }`}
          >
            {assignment.status === 1 ? "Active" : "Inactive"}
          </span>
        </div>
      </div>
    </div>
  );
};

const ShiftDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [shiftData, setShiftData] = useState(null);
  const [assignedUsers, setAssignedUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isChangeShiftModalOpen, setIsChangeShiftModalOpen] = useState(false);
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedNewShift, setSelectedNewShift] = useState(null);
  const [shiftOptions, setShiftOptions] = useState([]);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [effectiveFrom, setEffectiveFrom] = useState(moment().format("YYYY-MM-DD"));
  const [employeeOptions, setEmployeeOptions] = useState([]);
  const [loadingEmployees, setLoadingEmployees] = useState(false);
  const [loadingShifts, setLoadingShifts] = useState(false);
  const [userToRemove, setUserToRemove] = useState(null);
  const [isRemovingUser, setIsRemovingUser] = useState(false);

  // Fetch shift detail
  const fetchShiftDetail = useCallback(async () => {
    if (!id) return;
    
    setLoading(true);
    try {
      // Try to get shift detail with users
      // If API supports 'with' parameter, use it; otherwise fetch basic detail
      const response = await getShiftDetail(id);
    
      setShiftData(response?.workspaceShift);
      
      // API returns users array where each item has nested user object
      // Structure: response.workspaceShift.users = [{ id, uuid, effective_from, user: {...} }]
     
      if (response?.workspaceShift?.users && response.workspaceShift.users.length>0) {
        setAssignedUsers(response.workspaceShift.users);
      } else {
        setAssignedUsers([]);
      }
    } catch (error) {
      toast.error("Failed to fetch shift details");
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  // Fetch all shifts for change shift dropdown
  const fetchShiftOptions = useCallback(async () => {
    setLoadingShifts(true);
    try {
      const response = await getShiftList({ per_page: 1000 });
      const shifts = response?.workspaceShift?.data || [];
      const options = shifts
        .filter(shift => (shift.uuid || shift.id) !== id) // Exclude current shift
        .map(shift => ({
          value: shift.uuid || shift.id,
          label: `${shift.name} (${shift.start_time} - ${shift.end_time})`,
        }));
      setShiftOptions(options);
    } catch (error) {
      toast.error("Failed to fetch shifts");
      console.error(error);
    } finally {
      setLoadingShifts(false);
    }
  }, [id]);

  // Fetch employee list for assignment
  const fetchEmployeeList = useCallback(async () => {
    setLoadingEmployees(true);
    try {
      const response = await getUserList({
        with: "department,designation",
        per_page: 1000,
      });
      
      const employees = response?.user?.data || [];
      // Filter out already assigned users
      // assignedUsers is array of assignments: [{ id, uuid, user: { id, ... } }]
      const assignedUserIds = assignedUsers.map(assignment => {
        const user = assignment.user || {};
        return user.id; // Numeric user ID
      }).filter(id => id != null);
      
      const availableEmployees = employees.filter(
        emp => !assignedUserIds.includes(emp.id)
      );
      
      const options = availableEmployees.map((emp) => ({
        value: emp.id || emp.user_id || emp.uuid,
        label: `${emp.name || "Unknown"} (${emp.code || "N/A"})`,
        employee: emp,
      }));
      
      setEmployeeOptions(options);
    } catch (error) {
      console.error("Failed to fetch employees:", error);
      toast.error("Failed to load employee list");
      setEmployeeOptions([]);
    } finally {
      setLoadingEmployees(false);
    }
  }, [assignedUsers]);

  useEffect(() => {
    fetchShiftDetail();
    fetchShiftOptions();
  }, [fetchShiftDetail, fetchShiftOptions]);

  useEffect(() => {
    if (isAssignModalOpen) {
      fetchEmployeeList();
    }
  }, [isAssignModalOpen, fetchEmployeeList]);

  // Handle change user shift
  // assignment parameter is: { id, uuid, effective_from, user: {...} }
  const handleChangeShift = (assignment) => {
    setSelectedUser(assignment);
    setIsChangeShiftModalOpen(true);
  };

  // Confirm change shift
  const handleConfirmChangeShift = async () => {
    if (!selectedUser || !selectedNewShift || !effectiveFrom) {
      toast.error("Please fill all fields");
      return;
    }

    try {
      const userData = selectedUser.user || {};
      const userIdNumeric = userData.id; // Numeric user ID from nested user object

      // First unassign from current shift (remove from current shift's users list)
      const remainingUsers = assignedUsers
        .filter(assignment => {
          const user = assignment.user || {};
          return user.id !== userIdNumeric;
        })
        .map(assignment => {
          const user = assignment.user || {};
          return user.id; // Numeric user ID
        })
        .filter(id => id != null);

      const unassignPayload = {
        effective_from: moment().format("YYYY-MM-DD"),
        users: remainingUsers,
      };
      await bulkAssignShift(id, unassignPayload);

      // Then assign to new shift
      const assignPayload = {
        effective_from: effectiveFrom,
        users: [userIdNumeric],
      };

      await bulkAssignShift(selectedNewShift.value, assignPayload);
      toast.success("User shift changed successfully!");
      setIsChangeShiftModalOpen(false);
      setSelectedUser(null);
      setSelectedNewShift(null);
      setEffectiveFrom(moment().format("YYYY-MM-DD"));
      fetchShiftDetail();
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to change shift");
      console.error(error);
    }
  };

  // Handle unassign user
  // user parameter is the assignment object: { id, uuid, effective_from, user: {...} }
  const handleUnassignUser = (assignment) => {
    setUserToRemove(assignment);
  };

  const confirmUnassignUser = async () => {
    if (!userToRemove) return;

    const userData = userToRemove.user || {};
    const userName = userData.name || "this user";
    
    setIsRemovingUser(true);
    try {
      // Use PUT API to update shift with remaining users (remove the unassigned user)
      // Extract numeric user ID from nested user object
      const userIdToRemove = userData.id; // Numeric user ID
      
      const remainingUsers = assignedUsers
        .filter(assignment => {
          const user = assignment.user || {};
          return user.id !== userIdToRemove;
        })
        .map(assignment => {
          const user = assignment.user || {};
          return user.id; // Numeric user ID
        })
        .filter(id => id != null);

      // Update shift with remaining users
      const payload = {
        effective_from: moment().format("YYYY-MM-DD"),
        users: remainingUsers,
      };

      await bulkAssignShift(id, payload);
      toast.success(`${userName} unassigned successfully!`);
      setUserToRemove(null);
      fetchShiftDetail();
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to unassign user");
      console.error(error);
    } finally {
      setIsRemovingUser(false);
    }
  };

  // Handle assign new users
  const handleAssignNewUsers = () => {
    setIsAssignModalOpen(true);
  };

  // Confirm assign new users
  const handleConfirmAssignNewUsers = async () => {
    if (selectedEmployees.length === 0) {
      toast.error("Please select employees");
      return;
    }

    try {
      const payload = {
        effective_from: effectiveFrom,
        users: selectedEmployees.map(e => {
          const employee = e.employee || {};
          const userId = employee.id || employee.user_id || e.value;
          return typeof userId === 'string' ? parseInt(userId) : userId;
        }).filter(id => id != null),
      };

      await bulkAssignShift(id, payload);
      toast.success(`Successfully assigned ${selectedEmployees.length} employees to shift`);
      setIsAssignModalOpen(false);
      setSelectedEmployees([]);
      setEffectiveFrom(moment().format("YYYY-MM-DD"));
      fetchShiftDetail();
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to assign employees");
      console.error(error);
    }
  };

  // Transform assigned users data for table
  // API structure: user = { id, uuid, effective_from, user: { id, uuid, name, code, email, ... } }
  const transformedUsersData = useMemo(() => {
    if (!assignedUsers || assignedUsers.length === 0) {
      return [];
    }

    return assignedUsers.map((assignment) => {
      const user = assignment.user || {};
      
      return {
        id: {
          data: user.uuid || user.id || assignment.uuid || assignment.id,
          variant: "text",
        },
        name: {
          data: {
            name: user.name || "Unknown",
            email: user.email || "",
            id: user.code || "N/A",
            image: user.image || user.photo || user.profile_image || null,
            link: user.uuid ? `/hr/staff-directory/profile/${user.uuid}` : "#",
          },
          variant: "profile",
        },
        department: {
          data: user.department?.name || "N/A",
          variant: "text",
        },
        designation: {
          data: user.designation?.name || "N/A",
          variant: "text",
        },
        effective_from: {
          data: assignment.effective_from || "N/A",
          variant: "date",
        },
        actions: {
          data: user.uuid || user.id || assignment.uuid || assignment.id,
          variant: "text",
        },
        _originalData: assignment, // Store full assignment object with nested user
      };
    });
  }, [assignedUsers]);

  const userActionButtons = {
    changeShift: (item) => {
      const originalData = item._originalData;
      handleChangeShift(originalData);
    },
    unassign: (item) => {
      const originalData = item._originalData;
      handleUnassignUser(originalData);
    },
  };

  const userTableHeaders = [
    { label: "Employee Name", key: "name" },
    { label: "Department", key: "department" },
    { label: "Designation", key: "designation" },
    { label: "Effective From", key: "effective_from" },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading shift details...</p>
        </div>
      </div>
    );
  }

  if (!shiftData) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <p className="text-gray-600">Shift not found</p>
          <DefaultButton
            onClick={() => navigate("/hr/shift-editor")}
            className="mt-4"
            variant="primary"
          >
            Back to Shift Editor
          </DefaultButton>
        </div>
      </div>
    );
  }

  return (
    <div className="">
      <PageTableHeader
        breadcrumbs={items}
        title="Shift Detail"
      />
      {/* Shift Information Card */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-4">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-2xl font-semibold text-gray-900">{shiftData.name}</h2>
          <span className={`px-3 py-1 rounded-full text-sm font-medium ${
            shiftData.status === 1 || shiftData.status === "Active"
              ? "bg-success-50 text-success-700" 
              : "bg-gray-100 text-gray-700"
          }`}>
            {shiftData.status === 1 ? "Active" : shiftData.status === 0 ? "Inactive" : shiftData.status || "Active"}
          </span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
          <div className="flex items-center gap-3">
            <HiOutlineClock className="text-2xl text-primary-600" />
            <div>
              <p className="text-sm text-gray-500">Start Time</p>
              <p className="text-lg font-semibold text-gray-900">{shiftData.start_time || "N/A"}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <HiOutlineClock className="text-2xl text-primary-600" />
            <div>
              <p className="text-sm text-gray-500">End Time</p>
              <p className="text-lg font-semibold text-gray-900">{shiftData.end_time || "N/A"}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <HiOutlineUsers className="text-2xl text-primary-600" />
            <div>
              <p className="text-sm text-gray-500">Working Hours</p>
              <p className="text-lg font-semibold text-gray-900">
                {shiftData.required_working_hour || shiftData.working_hours_required || 0}h
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <HiOutlineUsers className="text-2xl text-primary-600" />
            <div>
              <p className="text-sm text-gray-500">Assigned Employees</p>
              <p className="text-lg font-semibold text-gray-900">
                {assignedUsers.length}
              </p>
            </div>
          </div>
        </div>

        {shiftData.description && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <p className="text-sm text-gray-500">Description</p>
            <p className="text-gray-700 mt-1">{shiftData.description}</p>
          </div>
        )}
      </div>

      {/* Assigned Users Section - Card View (like team detail page) */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="p-4 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">
            Assigned Users ({assignedUsers.length})
          </h3>
          <DefaultButton
            onClick={handleAssignNewUsers}
            variant="primary"
            icon={<HiOutlineUsers />}
            size="sm"
            disabled={isRemovingUser}
          >
            Add Users
          </DefaultButton>
        </div>
        <div className="p-6">
          {assignedUsers.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {assignedUsers.map((assignment, idx) => (
                <UserCard 
                  key={assignment.id || assignment.uuid || idx} 
                  assignment={assignment} 
                  onRemove={() => handleUnassignUser(assignment)}
                  isRemoving={isRemovingUser}
                />
              ))}
            </div>
          ) : (
            <div className="py-8">
              <NoData
                title="No Users Assigned"
                message="This shift doesn't have any assigned users yet."
                buttonText="Add Users"
                onClick={handleAssignNewUsers}
              />
            </div>
          )}
        </div>
      </div>

      {/* Change Shift Modal */}
      <CommonModal
        show={isChangeShiftModalOpen}
        onClose={() => {
          setIsChangeShiftModalOpen(false);
          setSelectedUser(null);
          setSelectedNewShift(null);
          setEffectiveFrom(moment().format("YYYY-MM-DD"));
        }}
        headerTitle="Change User Shift"
        modalSize="md"
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              User
            </label>
            <input
              type="text"
              value={selectedUser?.user?.name || "Unknown"}
              disabled
              className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50"
            />
          </div>
          <DefaultSearchSelectInput
            label="New Shift"
            options={shiftOptions}
            value={selectedNewShift}
            onChange={setSelectedNewShift}
            placeholder="Select new shift"
            loading={loadingShifts}
          />
          <DefaultDatePicker
            label="Effective From"
            value={effectiveFrom}
            onChange={(date) => setEffectiveFrom(moment(date).format("YYYY-MM-DD"))}
            minDate={moment().toDate()}
          />
          <div className="flex justify-end gap-2 pt-4">
            <DefaultButton
              onClick={() => {
                setIsChangeShiftModalOpen(false);
                setSelectedUser(null);
                setSelectedNewShift(null);
                setEffectiveFrom(moment().format("YYYY-MM-DD"));
              }}
              variant="secondary"
            >
              Cancel
            </DefaultButton>
            <DefaultButton
              onClick={handleConfirmChangeShift}
              variant="primary"
            >
              Change Shift
            </DefaultButton>
          </div>
        </div>
      </CommonModal>

      {/* Assign New Users Modal */}
      <CommonModal
        show={isAssignModalOpen}
        onClose={() => {
          setIsAssignModalOpen(false);
          setSelectedEmployees([]);
          setEffectiveFrom(moment().format("YYYY-MM-DD"));
        }}
        headerTitle="Assign Users to Shift"
        modalSize="md"
      >
        <div className="space-y-4">
          <DefaultSearchSelectInput
            label="Select Employees"
            options={employeeOptions}
            value={selectedEmployees}
            onChange={setSelectedEmployees}
            placeholder="Search and select employees"
            isMulti
            loading={loadingEmployees}
          />
          <DefaultDatePicker
            label="Effective From"
            value={effectiveFrom}
            onChange={(date) => setEffectiveFrom(moment(date).format("YYYY-MM-DD"))}
            minDate={moment().toDate()}
          />
          <div className="flex justify-end gap-2 pt-4">
            <DefaultButton
              onClick={() => {
                setIsAssignModalOpen(false);
                setSelectedEmployees([]);
                setEffectiveFrom(moment().format("YYYY-MM-DD"));
              }}
              variant="secondary"
            >
              Cancel
            </DefaultButton>
            <DefaultButton
              onClick={handleConfirmAssignNewUsers}
              variant="primary"
              disabled={selectedEmployees.length === 0}
            >
              Assign Users
            </DefaultButton>
          </div>
        </div>
      </CommonModal>

      {/* Remove User Confirmation Modal */}
      <CommonModal
        show={!!userToRemove}
        onClose={() => setUserToRemove(null)}
        headerTitle="Remove User from Shift"
        modalSize="md"
      >
        <div className="space-y-4">
          {userToRemove && (
            <>
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <p className="text-sm text-yellow-800">
                  Are you sure you want to remove <strong>{userToRemove.user?.name || "this user"}</strong> from this shift?
                </p>
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <DefaultButton
                  name="Cancel"
                  onClick={() => setUserToRemove(null)}
                  variant="secondary"
                  disabled={isRemovingUser}
                />
                <DefaultButton
                name= {isRemovingUser ? "Removing..." : "Remove User"}
                  onClick={confirmUnassignUser}
                  variant="danger"
                  disabled={isRemovingUser}
                />
              </div>
            </>
          )}
        </div>
      </CommonModal>
    </div>
  );
};

export default ShiftDetail;

